/**
 * CANONICAL NODE TYPE CONSTANTS
 * 
 * Single source of truth for all node type strings used throughout the application.
 * Using these constants provides:
 * - IDE autocomplete
 * - Compile-time error checking for typos
 * - Single place to update if types change
 * - Self-documenting code
 * 
 * @example
 * ```typescript
 * import { NODE_TYPES } from './types/nodeTypes';
 * 
 * if (item.type === NODE_TYPES.PARAGRAPH) {
 *   // TypeScript knows this is a paragraph
 * }
 * ```
 */

/**
 * Node type constants matching ProseMirror schema
 */
export const NODE_TYPES = {
  // Document structure
  DOC: 'doc',
  
  // Block nodes
  PARAGRAPH: 'paragraph',
  HEADING: 'heading',
  BLOCKQUOTE: 'blockquote',
  CODE_BLOCK: 'code_block',
  HORIZONTAL_RULE: 'horizontal_rule',
  HARD_BREAK: 'hard_break',
  
  // List nodes
  BULLET_LIST: 'bullet_list',
  ORDERED_LIST: 'ordered_list',
  LIST_ITEM: 'list_item',
  LIST: 'list', // Generic list type (used in marked.js tokens)
  
  // Table nodes
  TABLE: 'table',
  TABLE_ROW: 'table_row',
  TABLE_CELL: 'table_cell',
  TABLE_HEADER: 'table_header',
  
  // Math nodes (LaTeX)
  MATH_INLINE: 'math_inline',
  MATH_DISPLAY: 'math_display',
  
  // Media nodes
  IMAGE: 'image',
  
  // Text node
  TEXT: 'text',
} as const;

/**
 * TypeScript type derived from NODE_TYPES
 * Provides autocomplete and type checking
 */
export type NodeType = typeof NODE_TYPES[keyof typeof NODE_TYPES];

/**
 * Mark type constants (inline formatting)
 */
export const MARK_TYPES = {
  STRONG: 'strong',      // Bold
  EM: 'em',              // Italic/Emphasis
  CODE: 'code',          // Inline code
  STRIKE: 'strike',      // Strikethrough
  UNDERLINE: 'underline',
  LINK: 'link',
  
  // Additional mark types
  EMPHASIS: 'emphasis',  // Alternative to EM (marked.js uses this)
  DEL: 'del',           // Alternative to STRIKE
} as const;

/**
 * TypeScript type for mark types
 */
export type MarkType = typeof MARK_TYPES[keyof typeof MARK_TYPES];

/**
 * Token types from marked.js (for markdown parsing)
 */
export const TOKEN_TYPES = {
  SPACE: 'space',
  PARAGRAPH: 'paragraph',
  HEADING: 'heading',
  LIST: 'list',
  CODE: 'code',
  BLOCKQUOTE: 'blockquote',
  TABLE: 'table',
  HR: 'hr',
  THEMATIC_BREAK: 'thematicBreak',
  IMAGE: 'image',
  
  // Inline token types
  TEXT: 'text',
  STRONG: 'strong',
  EM: 'em',
  CODESPAN: 'codespan',
  LINK: 'link',
  DEL: 'del',
} as const;

export type TokenType = typeof TOKEN_TYPES[keyof typeof TOKEN_TYPES];

/**
 * Type guard to check if a string is a valid node type
 */
export function isValidNodeType(type: string): type is NodeType {
  return Object.values(NODE_TYPES).includes(type as NodeType);
}

/**
 * Type guard to check if a string is a valid mark type
 */
export function isValidMarkType(type: string): type is MarkType {
  return Object.values(MARK_TYPES).includes(type as MarkType);
}

/**
 * Type guard to check if a string is a valid token type
 */
export function isValidTokenType(type: string): type is TokenType {
  return Object.values(TOKEN_TYPES).includes(type as TokenType);
}

